import { Rule } from "antd/lib/form";
import DateRenderer from "components/DateRenderer";
import { Form } from "components/styleguide";
import DatePickerAvailableDaysWrapper from "components/styleguide/components/DatePickerAvailableDaysWrapper";
import { TimeSlotSessionResponseModel } from "generated/api";
import { EventLayoutContext } from "layouts/EventLayout/types";
import moment, { Moment } from "moment";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { toWallClockMoment } from "utils/dateUtils";

import styles from "./styles.module.less";

type ActiveRange = "start" | "end";

export default function SessionCell({
  disabledDate,
  editing,
  yupSync,
  index,
  title,
  type,
  record,
  children,
  form,
  defaultSessionDuration,
  onSessionValuesChange,
  duplicate,
  duplicateMessage,
  locked,
  ...restProps
}: {
  disabledDate: (date: Moment) => boolean;
  editing: boolean;
  yupSync: Rule;
  index: string;
  title: string;
  type: string;
  record?: TimeSlotSessionResponseModel;
  children: React.ReactNode[];
  form: any;
  defaultSessionDuration?: number;
  onSessionValuesChange?: () => void;
  duplicate?: boolean;
  duplicateMessage?: string;
  locked?: boolean;
}) {
  const { t } = useTranslation();
  const { forum } = useOutletContext<EventLayoutContext>();
  const [activeRange, setActiveRange] = useState<ActiveRange>("start");
  const startValueBeforeEdit = useRef<Moment | null>(null);
  const lastAppliedDefaultSessionDuration = useRef<number | undefined>(defaultSessionDuration);

  if (!record) {
    return <td {...restProps}>{children}</td>;
  }

  const isStartTime = index === "startTime";
  const isEndTime = index === "endTime";
  const sessionKey = Number(record.id) > 0 ? `id-${record.id}` : `order-${record.order}`;
  const durationFieldName = ["sessions", sessionKey, "duration"];
  const applyDefaultDurationFromStart = (nextDuration?: null | [Moment | null, Moment | null]) => {
    const nextStart = nextDuration?.[0];

    if (!nextStart || !defaultSessionDuration || defaultSessionDuration <= 0) {
      return;
    }

    const isSameStart = startValueBeforeEdit.current && moment(nextStart).isSame(moment(startValueBeforeEdit.current));
    const isSameDefaultSessionDuration = defaultSessionDuration === lastAppliedDefaultSessionDuration.current;

    if (isSameStart && isSameDefaultSessionDuration) {
      return;
    }

    const currentSessions = form.getFieldValue("sessions") ?? {};
    const nextEnd = moment(nextStart).add(defaultSessionDuration, "minutes");

    form.setFieldsValue({
      sessions: {
        ...currentSessions,
        [sessionKey]: {
          ...currentSessions[sessionKey],
          duration: [nextStart, nextEnd],
        },
      },
    });
    lastAppliedDefaultSessionDuration.current = defaultSessionDuration;
    onSessionValuesChange?.();
  };

  const renderRangePickerPanel = (panelNode: React.ReactNode) => (
    <div>
      <div className={styles.datePickerPanelTitles}>
        <span>{activeRange === "start" ? t("startTime") : t("endTime")}</span>
      </div>
      {panelNode}
    </div>
  );

  const handlePickerFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    const rangePicker = event.currentTarget.closest(".ant-picker-range");
    const inputs = rangePicker ? Array.from(rangePicker.querySelectorAll("input")) : [];
    const focusedInputIndex = inputs.indexOf(event.currentTarget);

    setActiveRange(focusedInputIndex === 1 ? "end" : "start");
  };

  if (editing && !locked) {
    if (isStartTime) {
      return (
        <td {...restProps} colSpan={2}>
          <Form.Item
            style={{ marginBottom: 0 }}
            name={durationFieldName}
            rules={[yupSync]}
            validateStatus={duplicate ? "error" : undefined}
            help={duplicate ? duplicateMessage ?? t("Session time already exists") : undefined}
          >
            <DatePickerAvailableDaysWrapper.RangePickerAvailableDaysWrapper
              style={{ width: "100%" }}
              showTime={true}
              minuteStep={5}
              placeholder={[t("startTime"), t("endTime")]}
              onFocus={handlePickerFocus}
              onCalendarChange={(_, __, info) => {
                setActiveRange(info.range);
              }}
              onOk={(values) => {
                if (activeRange === "start") {
                  applyDefaultDurationFromStart(values as [Moment | null, Moment | null]);
                }
              }}
              order={false}
              panelRender={renderRangePickerPanel}
              availableDatesGetterType="ActualDays"
              eventStartDate={toWallClockMoment(forum?.startDate)}
              eventEndDate={toWallClockMoment(forum?.endDate)}
              defaultPickerValue={[
                record?.startTime ? toWallClockMoment(record.startTime) : toWallClockMoment(forum?.startDate),
                record?.endTime ? toWallClockMoment(record.endTime) : toWallClockMoment(forum?.endDate),
              ]}
            />
          </Form.Item>
        </td>
      );
    } else if (isEndTime) {
      return <></>;
    }
  }

  return (
    <td {...restProps}>
      {(isStartTime || isEndTime) && children ? (
        <DateRenderer date={children[1] as string} showTime wallClock />
      ) : (
        children
      )}
    </td>
  );
}
